import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Shield, Menu, X, LogOut, LayoutDashboard, UserCheck, Settings, Bot, Award } from 'lucide-react';
import { Button } from '../ui/Button';
import { useAuth } from '../../context/AuthContext';
import { NotificationBell } from '../ui/NotificationBell';
import { useAI } from '../../context/AIContext';

import { useContent } from '../../context/ContentContext';

export const Navbar = () => {
    const [isOpen, setIsOpen] = useState(false);
    const { isAuthenticated, user, logout } = useAuth();
    const { isChatOpen, toggleChat } = useAI();
    // The legacy ContentContext branding doc represents per-tenant branding
    // (Sprint 9a moved that responsibility into the shell's AppLayout, where
    // branding is sourced from `organizations/{activeOrgId}.branding`). The
    // legacy public marketing chrome should always show the platform brand
    // regardless of what's in the legacy branding doc — otherwise stale
    // SBAJ-era content overrides the new copy. Same reason we ignore the
    // CMS-stored hero content in HomePage.
    const { menuItems } = useContent();
    const navigate = useNavigate();
    const location = useLocation();

    const handleLogout = () => {
        logout();
        navigate('/');
    };

    const isActive = (path: string) => location.pathname === path;

    // Filter visible items
    const visibleItems = menuItems.filter(item => item.isVisible);

    return (
        <nav className="bg-white/80 dark:bg-gray-900/80 dark:border-gray-800 backdrop-blur-md fixed w-full z-50 border-b border-gray-100 transition-colors duration-300">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="flex justify-between h-20 items-center">
                    <Link to="/" className="flex items-center space-x-3 group">
                        <div className="bg-gradient-to-br from-blue-600 to-purple-600 p-2 rounded-lg group-hover:scale-110 transition-transform duration-300">
                            <Shield className="h-8 w-8 text-white" />
                        </div>
                        <div>
                            <span className="text-2xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-blue-600 to-purple-600">
                                X-One Cyber Aware
                            </span>
                            <span className="block text-xs text-gray-500 dark:text-gray-300 font-medium tracking-wider">
                                CYBER AWARENESS PLATFORM
                            </span>
                        </div>
                    </Link>

                    <div className="hidden md:flex items-center space-x-8">
                        {/* Marketing-site nav: hardcoded so it shows up on a fresh
                            deploy without needing the legacy ContentContext to seed
                            menuItems. Hidden once authenticated — buyers don't need
                            to bounce back to /pricing once they're inside. */}
                        {!isAuthenticated && (
                            <>
                                <Link
                                    to="/how-it-works"
                                    className={`text-sm font-medium transition-colors hover:text-blue-600 dark:hover:text-blue-400 ${isActive('/how-it-works') ? 'text-blue-600 dark:text-blue-400' : 'text-gray-600 dark:text-gray-300'}`}
                                >
                                    How it works
                                </Link>
                                <Link
                                    to="/pricing"
                                    className={`text-sm font-medium transition-colors hover:text-blue-600 dark:hover:text-blue-400 ${isActive('/pricing') ? 'text-blue-600 dark:text-blue-400' : 'text-gray-600 dark:text-gray-300'}`}
                                >
                                    Pricing
                                </Link>
                            </>
                        )}
                        {visibleItems.map(item => (
                            <Link
                                key={item.id}
                                to={item.path}
                                className={`text-sm font-medium transition-colors hover:text-blue-600 dark:hover:text-blue-400 ${isActive(item.path) ? 'text-blue-600 dark:text-blue-400' : 'text-gray-600 dark:text-gray-300'}`}
                            >
                                {item.label}
                            </Link>
                        ))}


                        {isAuthenticated ? (
                            <div className="flex items-center gap-4 pl-4 border-l border-gray-200 dark:border-border">
                                <NotificationBell />
                                <Link to="/training/profile" className="hidden lg:block">
                                    <Button variant="outline" size="sm" className="text-gray-500 border-gray-200 hover:border-blue-600 dark:border-border">
                                        <Award className="h-5 w-5" />
                                    </Button>
                                </Link>
                                <button
                                    onClick={toggleChat}
                                    className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-full transition-colors relative"
                                    title="AI Assistant"
                                >
                                    <div className="flex items-center gap-2">
                                        <Bot className="h-5 w-5" />
                                        <span className="text-sm font-medium">AI</span>
                                    </div>
                                    {isChatOpen && <span className="absolute top-1 right-1 h-2 w-2 bg-green-500 rounded-full ring-2 ring-white"></span>}
                                </button>
                                {/* Forum is now part of dynamic menu if added, but keeping logic just in case user wants forced auth links */}
                                <Link to={user?.role === 'admin' ? '/admin' : user?.role === 'superadmin' ? '/superadmin' : '/dashboard'}>
                                    <Button variant="outline" size="sm" className="flex items-center">
                                        {user?.role === 'admin' ? <UserCheck className="h-4 w-4 mr-2" /> :
                                            user?.role === 'superadmin' ? <Settings className="h-4 w-4 mr-2" /> :
                                                <LayoutDashboard className="h-4 w-4 mr-2" />}
                                        Dashboard
                                    </Button>
                                </Link>
                                <Button variant="secondary" size="sm" onClick={handleLogout} className="flex items-center">
                                    <LogOut className="h-4 w-4 mr-2" />Logout
                                </Button>
                            </div>
                        ) : (
                            <div className="flex items-center gap-3 pl-4 border-l border-gray-200 dark:border-border">
                                <Link to="/signin"><Button variant="outline" size="sm">Sign in</Button></Link>
                                <Link to="/signup"><Button size="sm">Get started</Button></Link>
                            </div>
                        )}
                    </div>

                    <div className="md:hidden">
                        <button onClick={() => setIsOpen(!isOpen)} className="text-gray-600 hover:text-blue-600 transition-colors">
                            {isOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
                        </button>
                    </div>
                </div>
            </div>

            {/* Mobile Menu */}
            {isOpen && (
                <div className="md:hidden bg-white dark:bg-gray-900 border-t border-gray-100 dark:border-gray-800 absolute w-full shadow-xl">
                    <div className="px-4 pt-2 pb-6 space-y-2">
                        {!isAuthenticated && (
                            <>
                                <Link
                                    to="/how-it-works"
                                    onClick={() => setIsOpen(false)}
                                    className="block px-3 py-2 text-base font-medium text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg"
                                >
                                    How it works
                                </Link>
                                <Link
                                    to="/pricing"
                                    onClick={() => setIsOpen(false)}
                                    className="block px-3 py-2 text-base font-medium text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg"
                                >
                                    Pricing
                                </Link>
                            </>
                        )}
                        {visibleItems.map(item => (
                            <Link
                                key={item.id}
                                to={item.path}
                                className="block px-3 py-2 text-base font-medium text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg"
                                onClick={() => setIsOpen(false)}
                            >
                                {item.label}
                            </Link>
                        ))}

                        {isAuthenticated ? (
                            <div className="pt-4 mt-4 border-t border-gray-100 space-y-2 dark:border-border">
                                <Link to="/training/profile" className="block w-full">
                                    <Button variant="outline" className="w-full justify-start text-gray-600 border-gray-300 dark:border-border">
                                        <Award className="h-4 w-4 mr-2" /> My Profile
                                    </Button>
                                </Link>
                                <Link to={user?.role === 'admin' ? '/admin' : user?.role === 'superadmin' ? '/superadmin' : '/dashboard'} className="block w-full">
                                    <Button variant="outline" className="w-full justify-start">
                                        {user?.role === 'admin' ? <UserCheck className="h-4 w-4 mr-2" /> :
                                            user?.role === 'superadmin' ? <Settings className="h-4 w-4 mr-2" /> :
                                                <LayoutDashboard className="h-4 w-4 mr-2" />}
                                        Dashboard
                                    </Button>
                                </Link>
                                <Button variant="secondary" onClick={handleLogout} className="w-full justify-start">
                                    <LogOut className="h-4 w-4 mr-2" />Logout
                                </Button>
                            </div>
                        ) : (
                            <div className="pt-4 mt-4 border-t border-gray-100 grid grid-cols-2 gap-3 dark:border-border">
                                <Link to="/signin"><Button variant="outline" className="w-full">Sign in</Button></Link>
                                <Link to="/signup"><Button className="w-full">Get started</Button></Link>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </nav>
    );
};
